import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";
import { createFileRoute, Link, redirect, SearchParamError, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useToggle } from "usehooks-ts";
import z from "zod";

import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useHydrated } from "@/hooks/use-hydrated";
import { authClient } from "@/integrations/auth/client";
import { updateSupabasePasswordServerFn } from "@/integrations/auth/supabase-functions";
import { TERMS_PRIVACY_CONSENT_REQUIRED_MESSAGE } from "@/integrations/multiresume/compliance/consent";
import { isSupabaseAuthProvider } from "@/utils/auth-provider";
import { getUserFacingError } from "@/utils/user-facing-error";

const searchSchema = z.object({
  mode: z.enum(["invite", "recovery"]).optional(),
  token: z.string().min(1).optional(),
});

export const Route = createFileRoute("/auth/reset-password")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  beforeLoad: async ({ context }) => {
    if (context.flags.disableEmailAuth) throw redirect({ to: "/auth/login", replace: true });
  },
  onError: (error) => {
    if (error instanceof SearchParamError) {
      throw redirect({ to: "/auth/login" });
    }
  },
});

const formSchema = z.object({
  password: z.string().min(6).max(64),
  acceptedTermsAndPrivacy: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

function RouteComponent() {
  const navigate = useNavigate();
  const { mode, token } = Route.useSearch();
  const { flags } = Route.useRouteContext();
  const [showPassword, toggleShowPassword] = useToggle(false);
  const hydrated = useHydrated();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: "",
      acceptedTermsAndPrivacy: false,
    },
  });

  useEffect(() => {
    if (hydrated) form.setFocus("password");
  }, [form, hydrated]);

  const acceptedTermsAndPrivacy = form.watch("acceptedTermsAndPrivacy");
  const isSupabaseAuth = isSupabaseAuthProvider(flags.authProvider);

  const onSubmit = async (data: FormValues) => {
    const toastId = toast.loading(t`Resetting your password...`);

    if (isSupabaseAuth) {
      if (!data.acceptedTermsAndPrivacy) {
        toast.error(TERMS_PRIVACY_CONSENT_REQUIRED_MESSAGE, { id: toastId });
        return;
      }

      const result = await updateSupabasePasswordServerFn({
        data: { password: data.password, acceptedTermsAndPrivacy: data.acceptedTermsAndPrivacy, mode },
      });

      if (!result.ok) {
        toast.error(getUserFacingError({ message: result.error }, t`Could not reset your password. Please try again.`), {
          id: toastId,
        });
        return;
      }

      toast.success(t`Your password has been reset successfully.`, { id: toastId });
      void navigate({ to: "/dashboard" });
      return;
    }

    if (!token) {
      toast.error(t`Password reset token is missing.`, { id: toastId });
      return;
    }

    const { error } = await authClient.resetPassword({ token, newPassword: data.password });

    if (error) {
      toast.error(getUserFacingError(error, t`Could not reset your password. Please try again.`), { id: toastId });
      return;
    }

    toast.success(t`Your password has been reset successfully. You can now sign in with your new password.`, {
      id: toastId,
    });

    void navigate({ to: "/auth/login" });
  };

  return (
    <>
      <div className="space-y-1 text-center">
        <h1 className="text-2xl font-bold tracking-tight">
          {mode === "invite" ? <Trans>Set your password</Trans> : <Trans>Reset your password</Trans>}
        </h1>

        <div className="text-muted-foreground">
          {mode === "invite" ? (
            <Trans>Please choose a password for your invited account</Trans>
          ) : (
            <Trans>Please enter a new password for your account</Trans>
          )}
        </div>
      </div>

      <Form {...form}>
        <form method="post" onSubmit={form.handleSubmit(onSubmit)}>
          <fieldset disabled={!hydrated} className="m-0 min-w-0 space-y-6 border-0 p-0">
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <Trans>New Password</Trans>
                  </FormLabel>
                  <div className="flex items-center gap-x-1.5">
                    <FormControl
                      render={
                        <Input
                          min={6}
                          max={64}
                          type={showPassword ? "text" : "password"}
                          autoComplete="new-password"
                          {...field}
                        />
                      }
                    />

                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      aria-label={showPassword ? t`Hide password` : t`Show password`}
                      onClick={toggleShowPassword}
                    >
                      {showPassword ? <EyeIcon /> : <EyeSlashIcon />}
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isSupabaseAuth && (
              <FormField
                control={form.control}
                name="acceptedTermsAndPrivacy"
                render={({ field }) => (
                  <FormItem>
                    <label className="flex items-start gap-x-3 rounded-md border p-3 text-sm leading-relaxed">
                      <FormControl
                        render={
                          <input
                            type="checkbox"
                            checked={field.value}
                            onBlur={field.onBlur}
                            onChange={(event) => field.onChange(event.target.checked)}
                            className="mt-1 size-4 shrink-0 accent-primary"
                          />
                        }
                      />
                      <span>
                        <Trans>
                          I agree to the{" "}
                          <Link to="/terms" className="font-medium underline underline-offset-4">
                            Terms of Service
                          </Link>{" "}
                          and{" "}
                          <Link to="/privacy" className="font-medium underline underline-offset-4">
                            Privacy Policy
                          </Link>
                          .
                        </Trans>
                      </span>
                    </label>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={!hydrated || (isSupabaseAuth && !acceptedTermsAndPrivacy)}
            >
              <Trans>Reset Password</Trans>
            </Button>
          </fieldset>
        </form>
      </Form>
    </>
  );
}
